import { Fragment, useState } from "react";
import { ArrowDown2, ArrowRight2 } from "iconsax-react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { Table } from "@/shared/ui";
import { formatCurrencyNoDecimals, parseMoney } from "../../../utils";
import type { GroupVM, ProjectVM } from "../types";

interface Props {
  projects: ProjectVM[];
}

function amountText(value: string | undefined): string {
  const num = parseMoney(value ?? "");
  if (!value || num === 0) return "";
  if (num < 0) return `-${formatCurrencyNoDecimals(String(Math.abs(num)), true)}`;
  return formatCurrencyNoDecimals(value, true);
}

function isNegative(value: string | undefined): boolean {
  return parseMoney(value ?? "0") < 0;
}

export default function ProjectTableContent({ projects }: Props) {
  const { t } = useTranslation("ProjectTablePage");
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  function toggleProject(id: number) {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleGroup(key: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function renderGroup(group: GroupVM) {
    const isOpen = expandedGroups.has(group.key);
    const isIncome = group.kind === "income";
    return (
      <Fragment key={group.key}>
        <Table.Row selected className="surface-tertiary-fill cursor-pointer select-none" onClick={() => toggleGroup(group.key)}>
          <Table.Cell>
            <span className="flex justify-end pr-1">
              {isOpen ? <ArrowDown2 size={14} color="currentColor" /> : <ArrowRight2 size={14} color="currentColor" />}
            </span>
          </Table.Cell>
          <Table.Cell>{""}</Table.Cell>
          <Table.Cell colSpan={2} isBold className="pl-4">
            {group.label}
          </Table.Cell>
          <Table.Cell align="right" isBold>
            {isIncome ? amountText(group.total) : ""}
          </Table.Cell>
          <Table.Cell align="right" isBold>
            {isIncome ? "" : amountText(group.total)}
          </Table.Cell>
          <Table.Cell>{""}</Table.Cell>
        </Table.Row>
        {isOpen &&
          group.records.map(function renderRecord(record) {
            return (
              <Table.Row key={record.key}>
                <Table.Cell>{""}</Table.Cell>
                <Table.Cell className="content-base-secondary whitespace-nowrap">{record.date}</Table.Cell>
                <Table.Cell className="pl-8">{record.company}</Table.Cell>
                <Table.Cell className="content-base-secondary">{record.category}</Table.Cell>
                <Table.Cell align="right">{isIncome ? amountText(record.amount) : ""}</Table.Cell>
                <Table.Cell align="right">{isIncome ? "" : amountText(record.amount)}</Table.Cell>
                <Table.Cell>{""}</Table.Cell>
              </Table.Row>
            );
          })}
      </Fragment>
    );
  }

  return (
    <Table.Table striped={false} hoverable={false}>
      <Table.Header>
        <tr>
          <Table.HeadCell width="w-16">{t("table.index")}</Table.HeadCell>
          <Table.HeadCell width="w-28">{t("table.date")}</Table.HeadCell>
          <Table.HeadCell>{t("table.company")}</Table.HeadCell>
          <Table.HeadCell>{t("table.category")}</Table.HeadCell>
          <Table.HeadCell width="w-36" align="right">
            {t("table.income")}
          </Table.HeadCell>
          <Table.HeadCell width="w-36" align="right">
            {t("table.expense")}
          </Table.HeadCell>
          <Table.HeadCell width="w-36" align="right">
            {t("table.balance")}
          </Table.HeadCell>
        </tr>
      </Table.Header>
      <Table.Body>
        {projects.map(function renderProject(project, projectIndex) {
          const isOpen = expandedProjects.has(project.id);
          const showInitial = parseMoney(project.initialBalance) !== 0;
          return (
            <Fragment key={project.id}>
              <Table.Row
                selected
                className="surface-brand-subtle cursor-pointer select-none"
                onClick={() => toggleProject(project.id)}>
                <Table.Cell isBold>
                  <div className="flex items-center gap-1">
                    {isOpen ? (
                      <ArrowDown2 size={14} color="currentColor" />
                    ) : (
                      <ArrowRight2 size={14} color="currentColor" />
                    )}
                    {projectIndex + 1}
                  </div>
                </Table.Cell>
                <Table.Cell>{""}</Table.Cell>
                <Table.Cell colSpan={2} isBold>
                  {project.label}
                </Table.Cell>
                <Table.Cell align="right" isBold>
                  {amountText(project.totalIncome)}
                </Table.Cell>
                <Table.Cell align="right" isBold>
                  {amountText(project.totalExpense)}
                </Table.Cell>
                <Table.Cell align="right" isBold className={cn(isNegative(project.balance) && "content-action-negative")}>
                  {amountText(project.balance)}
                </Table.Cell>
              </Table.Row>

              {isOpen && (
                <>
                  {showInitial && (
                    <Table.Row>
                      <Table.Cell>{""}</Table.Cell>
                      <Table.Cell>{""}</Table.Cell>
                      <Table.Cell colSpan={2} className="pl-4 content-base-secondary">
                        {t("table.initialBalance")}
                      </Table.Cell>
                      <Table.Cell align="right">{amountText(project.initialBalance)}</Table.Cell>
                      <Table.Cell>{""}</Table.Cell>
                      <Table.Cell>{""}</Table.Cell>
                    </Table.Row>
                  )}
                  {project.incomeGroups.map(renderGroup)}
                  {project.expenseGroups.map(renderGroup)}
                  <Table.Row selected className="surface-secondary-fill">
                    <Table.Cell>{""}</Table.Cell>
                    <Table.Cell>{""}</Table.Cell>
                    <Table.Cell colSpan={2} isBold>
                      {t("table.total")}
                    </Table.Cell>
                    <Table.Cell align="right" isBold>
                      {amountText(project.totalIncome)}
                    </Table.Cell>
                    <Table.Cell align="right" isBold>
                      {amountText(project.totalExpense)}
                    </Table.Cell>
                    <Table.Cell
                      align="right"
                      isBold
                      className={cn(isNegative(project.balance) && "content-action-negative")}>
                      {amountText(project.balance)}
                    </Table.Cell>
                  </Table.Row>
                </>
              )}
            </Fragment>
          );
        })}
      </Table.Body>
    </Table.Table>
  );
}
